import { useCallback, useContext, useMemo, useRef, useState } from 'react'

import { type SanityCombinedListingProductFragment } from '@data/sanity/queries/types/product'
import {
  type CombinedListingStep,
  type CombinedListingToggleStepHandler,
  getCombinedListingProductDropdownOptions,
  ProductCombinedListingContext,
} from '@lib/product/combined-listing'
import { getProductGalleryPhoto } from '@lib/product/images'
import { StringsContext } from '@lib/strings-context'

import OptionThumbnail from './combined-listing-option-thumbnail'
import CombinedListingProductBackboardSelect from './combined-listing-product-backboard-select'
import CombinedListingProductGlassSelect from './combined-listing-product-glass-select'
import CombinedListingProductPassepartoutSelect from './combined-listing-product-passepartout-select'
import CombinedListingProductSizeSelect from './combined-listing-product-size-select'
import CombinedListingProductSpacerSelect from './combined-listing-product-spacer-select'
import SmallGraphic from './small-graphic'

interface CombinedListingProductSelectProps {
  frameColor?: string
  openStep?: CombinedListingStep
  isScreenSmallOrLarger: boolean
  onToggleStep: CombinedListingToggleStepHandler
  onMainProductChange: (product: SanityCombinedListingProductFragment) => void
}

const CombinedListingProductSelect = ({
  openStep,
  frameColor,
  isScreenSmallOrLarger,
  onToggleStep,
  onMainProductChange,
}: CombinedListingProductSelectProps) => {
  const strings = useContext(StringsContext)
  const {
    combinedListingSettings,
    combinedListingConfiguration,
    combinedListingMainProductId,
    combinedListingActiveProducts,
    combinedListingActiveSize,
  } = useContext(ProductCombinedListingContext)

  const [passepartoutHoleWidth, setPassepartoutHoleWidth] = useState<
    number | null
  >(null)
  const [passepartoutHoleHeight, setPassepartoutHoleHeight] = useState<
    number | null
  >(null)

  const isDefaultSelectionLoadedRef = useRef(false)

  // Product IDs for dropdown input values
  const glassProductId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'glass',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const backboardProductId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'backboard',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const passepartoutProductId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'passepartout',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const spacerProductId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'spacer',
      )?.productID,
    [combinedListingActiveProducts],
  )

  const frameOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.frame ?? [],
        null,
      ),
    [combinedListingConfiguration],
  )

  const minimumPassepartoutSize = useMemo(() => {
    return combinedListingSettings?.minimumPassepartoutSize ?? 1
  }, [combinedListingSettings])

  // const isEmptyFrame = useMemo(() => {
  //   return getIsEmptyFrame(combinedListingActiveProducts ?? [])
  // }, [combinedListingActiveProducts])

  const handleFrameChange = useCallback(
    (newProductId: string) => {
      if (!newProductId) {
        return
      }

      const newProduct = combinedListingConfiguration?.frame?.find(
        (product) => product.productID === Number(newProductId),
      )

      if (!newProduct) {
        return
      }

      onMainProductChange(newProduct)
    },
    [combinedListingConfiguration, onMainProductChange],
  )

  return (
    <div className="">
      <label className="inline-flex gap-x-1.5 items-center font-medium">
        {/* <Icon
          name="Info"
          id="frame-color-icon"
          className="text-gray-500 text-lg"
        /> */}

        {!!frameColor && (
          <>
            {strings.combinedListingFrameColor}:{' '}
            <span className="!font-normal">{frameColor}</span>
          </>
        )}
        {!frameColor && <> {strings.combinedListingFrame} </>}
      </label>

      <div className="flex flex-wrap gap-2.5 mt-3">
        {frameOptions.map((frameOption) => {
          const product = combinedListingConfiguration?.frame.find(
            (frame) => frame.productID === Number(frameOption.value),
          )

          const photo = getProductGalleryPhoto(
            product?.galleryPhotos ?? [],
            product?.listingPhotos?.[0].forOption,
            product?.listingPhotos?.[0].galleryImage,
          )

          if (photo) {
            return (
              <OptionThumbnail
                key={frameOption.value}
                photo={photo}
                active={
                  combinedListingMainProductId === Number(frameOption.value)
                }
                onClick={() => handleFrameChange(frameOption.value)}
              />
            )
          }

          return (
            <SmallGraphic
              key={frameOption.value}
              selected={
                combinedListingMainProductId === Number(frameOption.value)
              }
              backgroundColor={frameOption.color}
              backgroundImage={frameOption.imageUrl}
              onClick={() => handleFrameChange(frameOption.value)}
            />
          )
        })}
      </div>

      <div className="space-y-3 mt-6">
        <h5 className="font-medium">{strings.combinedListingConfigureFrame}</h5>

        <CombinedListingProductSizeSelect
          minimumPassepartoutSize={minimumPassepartoutSize}
          defaultPassepartoutSizePercentage={
            combinedListingSettings?.defaultPassepartoutSizePercentage
          }
          isDefaultSelectionLoadedRef={isDefaultSelectionLoadedRef}
          glassProductId={glassProductId}
          backboardProductId={backboardProductId}
          passepartoutProductId={passepartoutProductId}
          spacerProductId={spacerProductId}
          openStep={openStep}
          isScreenSmallOrLarger={isScreenSmallOrLarger}
          setPassepartoutHoleWidth={setPassepartoutHoleWidth}
          setPassepartoutHoleHeight={setPassepartoutHoleHeight}
          onToggleStep={onToggleStep}
        />

        {!!combinedListingActiveSize && (
          <>
            {/* {isEmptyFrame && (
              <div className="inline-block px-4 rounded-md bg-gray-700">
                {strings.combinedListingEmptyFrame}
              </div>
            )} */}

            <CombinedListingProductGlassSelect
              isDefaultSelectionLoadedRef={isDefaultSelectionLoadedRef}
              glassProductId={glassProductId}
              openStep={openStep}
              isScreenSmallOrLarger={isScreenSmallOrLarger}
              onToggleStep={onToggleStep}
            />

            <CombinedListingProductBackboardSelect
              isDefaultSelectionLoadedRef={isDefaultSelectionLoadedRef}
              backboardProductId={backboardProductId}
              openStep={openStep}
              isScreenSmallOrLarger={isScreenSmallOrLarger}
              onToggleStep={onToggleStep}
            />

            <CombinedListingProductPassepartoutSelect
              minimumPassepartoutSize={minimumPassepartoutSize}
              passepartoutHoleWidth={passepartoutHoleWidth}
              passepartoutHoleHeight={passepartoutHoleHeight}
              isDefaultSelectionLoadedRef={isDefaultSelectionLoadedRef}
              passepartoutProductId={passepartoutProductId}
              openStep={openStep}
              isScreenSmallOrLarger={isScreenSmallOrLarger}
              setPassepartoutHoleWidth={setPassepartoutHoleWidth}
              setPassepartoutHoleHeight={setPassepartoutHoleHeight}
              onToggleStep={onToggleStep}
            />

            <CombinedListingProductSpacerSelect
              spacerProductId={spacerProductId}
              openStep={openStep}
              isScreenSmallOrLarger={isScreenSmallOrLarger}
              onToggleStep={onToggleStep}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default CombinedListingProductSelect
